import React, { FC, useCallback, useRef, memo, useMemo, useState } from 'react'
import debounce from 'lodash/debounce'

import { useSearchArtistByName } from 'modules/ProjectCreate/serviceHooks/useSearchArtist'
import { useController, useFormContext } from 'react-hook-form'
import { useLoading, useServerError } from 'shared/hooks'
import { AsyncSelect, InputGroup } from '@wmgtech/legato'
import { SearchItem } from 'components/SearchItem'
import createSubtitle from 'shared/utils/createArtistInfoSubtitle'
import { rules } from 'modules/ProjectEdit/constants'
import { IProjectForm } from 'modules/ProjectEdit/types'

export const ArtistAutocomplete: FC<any> = memo(({artist, control}) => {
  const [isSearchedValue, setSearched] = useState(false)
  const { getValues, reset } = useFormContext<IProjectForm>()
  const { getArtistByName, loading: artistLoading, error: artistError, data: artistList, getArtistResultById } = useSearchArtistByName()
  const {
    field,
    fieldState: { error }
  } = useController({
    name: 'searchedName',
    control,
    rules: rules.searchedName
  })

  const validation = useMemo(() => {
    if (error && error?.message) {
      return {
        message: error.message,
        ...(error && { type: 'error' })
      }
    }
    return {}
  }, [error])

  const dvOptions = useMemo(() => artistList?.getArtistsByName ?? [artist], [artistList?.getArtistsByName, artist])

  const handleSelect = useCallback<(value: any) => void>(
    (value) => {
      const artist = getArtistResultById(value?.id)
      reset({
        ...getValues(),
        artistId: value?.id,
        searchedName: artist?.name,
        artistName: artist?.name,
        opusId: artist?.opus_id,
        gpsStatus: artist?.gps_status,
        artistLabelId: artist?.label_id
      })
    },[getArtistResultById, getValues, reset]
  )

  const searchArtists = useCallback<(searchValue: string) => void>(
    (searchValue) => {
      setSearched(true)
      return getArtistByName(searchValue)
    },[getArtistByName, setSearched]
  )

  const debouncedSearchArtist = useRef<(term: string) => void>(debounce(searchArtists, 400))

  useLoading({ isLoading: artistLoading })
  useServerError([artistError])

  return (
    <InputGroup label="Artist" labelProps={{ icon: 'asterisk' }} validation={validation}>
      <AsyncSelect
        {...field}
        list={dvOptions}
        getTitle={({ name }) => name}
        getKey={({ id, name }) => isSearchedValue ? id : name}
        onSelect={handleSelect}
        onSearch={debouncedSearchArtist.current}
        placeholder="Start typing Artist name..."
        getElement={({ name, gcdm_info }) => (
          <SearchItem title={name} subtitle={createSubtitle(gcdm_info?.genre, gcdm_info?.country, gcdm_info?.company)} />
        )}
      />
    </InputGroup>
  )
})
